import React, { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
    Alert,
    Button,
    Card,
    Collapse,
    Input,
    Select,
    Space,
    Switch,
    Table,
    Typography,
    message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { ITEM_TYPES, MODEL_ENGINES, MODEL_ITEM_TYPES } from '../itemTypes';
import { VARIATION_TYPES } from '../variationTypesConfig';
import { toSentenceCase } from '../ingestionHelpers';

const { Title, Text } = Typography;

// Order-independent so a variant's purchase-URL override matches its combo
// regardless of how the variation ids happen to be ordered on load vs. storage.
const comboKeyOf = (tempIds) => [...tempIds].sort().join('|');

function cartesian(variationTypes) {
    const withVariations = variationTypes.filter((vt) => vt.variations.length > 0);
    if (withVariations.length === 0) {
        return [[]];
    }
    return withVariations.reduce(
        (combos, vt) =>
            combos.flatMap((combo) => vt.variations.map((variation) => [...combo, variation])),
        [[]],
    );
}

function toInitialState(item) {
    if (!item) {
        return {
            name: '',
            itemType: undefined,
            modelEngine: 'opus',
            mode: 'auto',
            variationTypes: [],
            overrides: {},
        };
    }
    // Primary type first: submit derives isPrimary from position (index === 0),
    // so the loaded order must put the real primary first or it gets flipped.
    const variationTypes = (item.variationTypes || [])
        .slice()
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
        .map((vt) => ({
            key: vt.id,
            title: vt.title,
            isPrimary: vt.isPrimary,
            variations: (vt.variations || []).map((v) => ({ tempId: v.id, title: v.title })),
        }));
    const overrides = {};
    (item.variants || []).forEach((variant) => {
        overrides[comboKeyOf(variant.variationIds || [])] = {
            purchaseUrl: variant.purchaseUrl || '',
        };
    });
    return { name: item.name, itemType: item.itemType, modelEngine: item.modelEngine || 'meshy', mode: item.mode || 'auto', variationTypes, overrides };
}

export function Step1Variations({ item, saving, onSubmit }) {
    const initial = useMemo(() => toInitialState(item), [item]);
    const [name, setName] = useState(initial.name);
    const [itemType, setItemType] = useState(initial.itemType);
    const [modelEngine, setModelEngine] = useState(initial.modelEngine);
    const [mode, setMode] = useState(initial.mode);
    const [variationTypes, setVariationTypes] = useState(initial.variationTypes);
    const [overrides, setOverrides] = useState(initial.overrides);
    const [importText, setImportText] = useState('');

    const combos = useMemo(() => cartesian(variationTypes), [variationTypes]);

    const updateType = (key, patch) =>
        setVariationTypes((types) => types.map((t) => (t.key === key ? { ...t, ...patch } : t)));

    const addType = () =>
        setVariationTypes((types) => [
            ...types,
            { key: uuid(), title: '', isPrimary: types.length === 0, variations: [] },
        ]);

    const removeType = (key) => setVariationTypes((types) => types.filter((t) => t.key !== key));

    // The first variation type is the primary one (shown in the catalog), so
    // "make primary" just moves it to the front.
    const makePrimary = (key) =>
        setVariationTypes((types) => {
            const idx = types.findIndex((t) => t.key === key);
            if (idx <= 0) return types;
            const next = [...types];
            const [picked] = next.splice(idx, 1);
            return [picked, ...next];
        });

    const addVariation = (typeKey) =>
        updateType(typeKey, {
            variations: [
                ...variationTypes.find((t) => t.key === typeKey).variations,
                { tempId: uuid(), title: '' },
            ],
        });

    const updateVariation = (typeKey, tempId, title) =>
        updateType(typeKey, {
            variations: variationTypes
                .find((t) => t.key === typeKey)
                .variations.map((v) => (v.tempId === tempId ? { ...v, title } : v)),
        });

    const removeVariation = (typeKey, tempId) =>
        updateType(typeKey, {
            variations: variationTypes.find((t) => t.key === typeKey).variations.filter((v) => v.tempId !== tempId),
        });

    const getOverride = (key) => overrides[key] || { purchaseUrl: '' };
    const setOverride = (key, patch) =>
        setOverrides((o) => ({ ...o, [key]: { ...getOverride(key), ...patch } }));

    const handleSubmit = () => {
        if (!name.trim()) return message.error('Name is required');
        if (!itemType) return message.error('Item type is required');
        for (const vt of variationTypes) {
            if (!vt.title.trim()) return message.error('Every variation type needs a title');
            // cartesian() skips empty types, so an empty PRIMARY type would
            // submit variants linked only to the secondary types: the item
            // publishes with no tile and never shows up in search.
            if (vt.variations.length === 0)
                return message.error(`Variation type "${vt.title.trim()}" needs at least one variation`);
            if (vt.variations.some((v) => !v.title.trim()))
                return message.error('Every variation needs a name');
        }
        // A secondary type with one variation adds nothing to tell variants apart;
        // that detail belongs in the item name or the primary type.
        const lone = variationTypes.slice(1).find((vt) => vt.variations.length === 1);
        if (lone) return message.error(`Variation type "${lone.title.trim()}" has a single variation — add another or remove the type`);

        const payload = {
            name: name.trim(),
            itemType,
            modelEngine,
            mode,
            variationTypes: variationTypes.map((vt, index) => ({
                title: vt.title.trim(),
                isPrimary: index === 0,
                variations: vt.variations.map((v) => ({ tempId: v.tempId, title: v.title.trim() })),
            })),
            variants: combos.map((combo) => {
                const key = comboKeyOf(combo.map((v) => v.tempId));
                const override = getOverride(key);
                return {
                    variationTempIds: combo.map((v) => v.tempId),
                    purchaseUrl: override.purchaseUrl ? override.purchaseUrl.trim() : null,
                };
            }),
        };
        onSubmit(payload);
    };

    // Dev tools: round-trip only the data set here (name, type, variation types +
    // their variations, and per-variant URLs) — nothing scraped/extracted.
    const buildExportJson = () => ({
        name: name.trim(),
        itemType,
        modelEngine,
        mode,
        variationTypes: variationTypes.map((vt) => ({
            title: vt.title,
            variations: vt.variations.map((v) => v.title),
        })),
        variants: combos.map((combo) => {
            const key = comboKeyOf(combo.map((v) => v.tempId));
            return {
                variations: combo.map((v) => v.title),
                purchaseUrl: getOverride(key).purchaseUrl || null,
            };
        }),
    });

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(buildExportJson(), null, 2));
            message.success('Variations JSON copied to clipboard');
        } catch (e) {
            message.error('Could not copy to clipboard');
        }
    };

    const applyImport = () => {
        let data;
        try {
            data = JSON.parse(importText);
        } catch (e) {
            return message.error('Invalid JSON');
        }
        const newTypes = (data.variationTypes || []).map((t, i) => ({
            key: uuid(),
            title: t.title || '',
            isPrimary: i === 0,
            variations: (t.variations || []).map((title) => ({ tempId: uuid(), title })),
        }));
        // Match each variant's variation titles back to the freshly-minted tempIds.
        const nextOverrides = {};
        (data.variants || []).forEach((variant) => {
            const tempIds = (variant.variations || [])
                .map((title) => {
                    for (const t of newTypes) {
                        const found = t.variations.find((v) => v.title === title);
                        if (found) return found.tempId;
                    }
                    return null;
                })
                .filter(Boolean);
            if (tempIds.length && variant.purchaseUrl) {
                nextOverrides[comboKeyOf(tempIds)] = { purchaseUrl: variant.purchaseUrl };
            }
        });
        setName(data.name || '');
        setItemType(data.itemType || undefined);
        setMode(data.mode === 'manual' ? 'manual' : 'auto');
        setVariationTypes(newTypes);
        setOverrides(nextOverrides);
        message.success('Imported — review and create the item');
    };

    const variantColumns = [
        {
            title: 'Variant',
            key: 'combo',
            render: (_, row) => row.label,
        },
        {
            title: 'Purchase URL',
            key: 'url',
            render: (_, row) => (
                <Input
                    placeholder="https://… (auto-fetched if set)"
                    value={getOverride(row.key).purchaseUrl}
                    onChange={(e) => setOverride(row.key, { purchaseUrl: e.target.value })}
                />
            ),
        },
    ];

    // A variation type with a single variation adds no distinction between
    // variants, so omit it from the label (unless every type is single).
    const distinguishingTypes = variationTypes.filter((vt) => vt.variations.length > 1);
    const labelTypes = distinguishingTypes.length ? distinguishingTypes : variationTypes;

    const variantRows = combos.map((combo) => {
        const key = comboKeyOf(combo.map((v) => v.tempId));
        const comboIds = new Set(combo.map((v) => v.tempId));
        const label = labelTypes
            .map((vt) => vt.variations.find((v) => comboIds.has(v.tempId)))
            .filter(Boolean)
            .map((v) => v.title || '—')
            .join(' / ');
        return { key, label: label || 'Single variant' };
    });

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                        <Text>Name</Text>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => setName((n) => toSentenceCase(n))}
                            placeholder="Item name"
                        />
                    </div>
                    <Space size="large" wrap>
                        <div>
                            <Text>Item type</Text>
                            <br />
                            <Select
                                style={{ width: 220 }}
                                placeholder="Select type"
                                value={itemType}
                                onChange={setItemType}
                                options={ITEM_TYPES.map((t) => ({ value: t, label: t }))}
                            />
                        </div>
                        {MODEL_ITEM_TYPES.includes(itemType) && (
                            <div>
                                <Text>3D model by</Text>
                                <br />
                                <Select
                                    style={{ width: 220 }}
                                    value={modelEngine}
                                    onChange={setModelEngine}
                                    options={MODEL_ENGINES.map((e) => ({ value: e.value, label: e.label }))}
                                />
                            </div>
                        )}
                    </Space>
                </Space>
            </Card>

            <Card title="Pipeline mode">
                <Space align="center" size="middle">
                    <Switch
                        checked={mode === 'auto'}
                        onChange={(checked) => setMode(checked ? 'auto' : 'manual')}
                    />
                    <Text strong>{mode === 'auto' ? 'Auto' : 'Manual'}</Text>
                </Space>
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                        <strong>Auto</strong> runs the item through every step automatically (scrape → extract →
                        assets → compress → confirm) without waiting for you. <strong>Manual</strong> stops after
                        each step so you can trigger the next one yourself. Either way, if a step fails or needs a
                        decision, the item drops back to manual so it can't run away.
                    </Text>
                </div>
            </Card>

            <Card
                title="Variation types"
                extra={
                    <Button icon={<PlusOutlined />} onClick={addType}>
                        Add variation type
                    </Button>
                }
            >
                {variationTypes.length === 0 && (
                    <Text type="secondary">No variation types — the item will have a single variant.</Text>
                )}
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {variationTypes.map((vt, vtIndex) => (
                        <Card key={vt.key} size="small" type="inner">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Space wrap>
                                    <Select
                                        style={{ width: 220 }}
                                        placeholder="Choose type…"
                                        value={vt.title || undefined}
                                        onChange={(value) => updateType(vt.key, { title: value })}
                                        options={
                                            vt.title && !VARIATION_TYPES.some((o) => o.value === vt.title)
                                                ? [...VARIATION_TYPES, { value: vt.title, label: vt.title }]
                                                : VARIATION_TYPES
                                        }
                                    />
                                    {vtIndex !== 0 && (
                                        <Button
                                            onClick={() => makePrimary(vt.key)}
                                        >
                                            Make primary
                                        </Button>
                                    )}
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeType(vt.key)}
                                    >
                                        Remove
                                    </Button>
                                </Space>
                                {vt.variations.map((v) => (
                                    <Space key={v.tempId}>
                                        <Input
                                            style={{ width: 220 }}
                                            placeholder="Variation name (e.g. Blue)"
                                            value={v.title}
                                            onChange={(e) => updateVariation(vt.key, v.tempId, e.target.value)}
                                            onBlur={() => updateVariation(vt.key, v.tempId, toSentenceCase(v.title))}
                                        />
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeVariation(vt.key, v.tempId)}
                                        />
                                    </Space>
                                ))}
                                <Button type="dashed" icon={<PlusOutlined />} onClick={() => addVariation(vt.key)}>
                                    Add variation
                                </Button>
                                {vtIndex === 0 && (
                                    <Alert
                                        type="info"
                                        showIcon
                                        message="This is the primary variation type and will be displayed in the catalog"
                                    />
                                )}
                            </Space>
                        </Card>
                    ))}
                </Space>
            </Card>

            <Card title={`Variants (${variantRows.length})`}>
                <Table
                    size="small"
                    rowKey="key"
                    pagination={false}
                    columns={variantColumns}
                    dataSource={variantRows}
                />
            </Card>

            <Button
                type="primary"
                size="large"
                loading={saving}
                onClick={handleSubmit}
            >
                {item ? 'Save variations' : 'Create pipeline item'}
            </Button>

            <Collapse
                items={[
                    {
                        key: 'dev-tools',
                        label: 'Dev tools',
                        children: item ? (
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">
                                    Copies only what's set here (name, item type, variation types, variant URLs) —
                                    no scraped or extracted data. Paste it into the “Dev tools” card on a new item.
                                </Text>
                                <Button onClick={copyJson}>Copy variations as JSON</Button>
                            </Space>
                        ) : (
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">
                                    Paste a variations JSON (copied from another item) to pre-fill the form, then
                                    create the item.
                                </Text>
                                <Input.TextArea
                                    rows={6}
                                    value={importText}
                                    onChange={(e) => setImportText(e.target.value)}
                                    placeholder='{"name":"…","itemType":"…","variationTypes":[…],"variants":[…]}'
                                />
                                <Button onClick={applyImport} disabled={!importText.trim()}>
                                    Apply JSON
                                </Button>
                            </Space>
                        ),
                    },
                ]}
            />
        </Space>
    );
}
